import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ExternalLink, MapPin, Phone, Smartphone, Mail, Instagram, Check, X, Send, Play, Home, Building2, Warehouse, Hammer, ClipboardList, BedDouble, Bath, Ruler } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import EnquiryDialog from "../components/site/EnquiryDialog";
import ShowHomeTour from "../components/site/ShowHomeTour";
import BuilderGallerySlider from "../components/site/BuilderGallerySlider";
import LotDetailDialog from "../components/site/LotDetailDialog";
import { SITE_URL } from "../seo/seoConfig";
import { usePageMetaEffect } from "../seo/pageMeta";

const serviceIcon = (label) => {
  const l = (label || "").toLowerCase();
  if (l.includes("garage")) return Warehouse;
  if (l.includes("duplex") || l.includes("multi")) return Building2;
  if (l.includes("spec")) return Hammer;
  if (l.includes("project") || l.includes("management") || l.includes("consult")) return ClipboardList;
  if (l.includes("residential")) return Building2;
  return Home;
};

const instaUrl = (h) => (!h ? null : h.startsWith("http") ? h : `https://instagram.com/${h.replace(/^@/, "")}`);

const BuilderDetail = () => {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [enq, setEnq] = useState(false);
  const [tour, setTour] = useState(false);
  const [availStage, setAvailStage] = useState(null);
  const [stageParcels, setStageParcels] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/builders/${id}`).then((r) => setB(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    api.get("/stages").then((r) => {
      const st = (r.data || []).find((s) => s.status === "available");
      if (!st) return;
      setAvailStage(st);
      api.get(`/parcels?stage_id=${st.id}`).then((pr) => {
        setStageParcels(pr.data || []);
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  usePageMetaEffect(
    b ? `${b.name} · Custom Home Builder · Deer Park Estates` : null,
    b ? (b.tagline || b.description || "Custom home builder at Deer Park Estates, Spruce Grove, Alberta.").slice(0, 160) : null,
  );

  if (!b) return <div className="pt-[120px] text-center text-[#F3EEE3]/50">Loading…</div>;
  const cover = b.gallery?.[0];

  const origin = SITE_URL;
  const builderUrl = `${origin}/builders/${id}`;
  const sameAs = [b.website, instaUrl(b.instagram)].filter(Boolean);
  const builderLd = Object.fromEntries(
    Object.entries({
      "@context": "https://schema.org",
      "@type": "GeneralContractor",
      name: b.name,
      description: b.tagline || b.description || undefined,
      url: builderUrl,
      logo: b.logo_url ? assetUrl(b.logo_url) : undefined,
      image: cover ? assetUrl(cover) : undefined,
      telephone: b.phone || b.cell || undefined,
      email: b.email || undefined,
      address: {
        "@type": "PostalAddress",
        addressLocality: b.locale || "Spruce Grove",
        addressRegion: "AB",
        addressCountry: "CA",
      },
      areaServed: {
        "@type": "Place",
        name: "Deer Park Estates, Spruce Grove, Alberta",
      },
      ...(sameAs.length ? { sameAs } : {}),
    }).filter(([, v]) => v !== undefined)
  );

  const sortLots = (a, c) => String(a.block || "").localeCompare(String(c.block || "")) || (a.lot_number || 0) - (c.lot_number || 0);
  const nm = (s) => (s || "").toLowerCase().trim();
  const myLots = stageParcels
    .filter((p) => p.builder_name && (nm(b.name).includes(nm(p.builder_name)) || nm(p.builder_name).includes(nm(b.name))))
    .sort(sortLots);
  const availLots = stageParcels
    .filter((p) => p.status === "available" && !p.builder_name)
    .sort(sortLots);

  const photoPool = [
    ...(b.gallery || []),
    ...((b.quick_possessions || []).map((h) => h.image).filter(Boolean)),
    ...((b.models || []).map((m) => m.image).filter(Boolean)),
  ];
  const openLot = (p, i, reserved) => {
    const areaTxt = p.area_sqft ? `${Number(p.area_sqft).toLocaleString()} sq ft` : "an estate-scale";
    const frontTxt = p.frontage_m ? ` with ${p.frontage_m} m of frontage` : "";
    const walkTxt = p.walkout ? "walkout " : "";
    const stageName = availStage?.name || "this stage";
    const desc = reserved
      ? `A ${walkTxt}lot of ${areaTxt}${frontTxt} in ${stageName}, secured by ${b.name} for an upcoming custom home.`
      : `A ${walkTxt}lot of ${areaTxt}${frontTxt} in ${stageName}, available to build a ${b.name} home${b.models?.[0]?.name ? ` such as the ${b.models[0].name}` : ""}.`;
    setDetail({ parcel: p, photo: photoPool.length ? photoPool[i % photoPool.length] : null, description: desc });
  };

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(builderLd)}</script>
      </Helmet>
      {/* hero */}
      <section className="relative h-[46vh] min-h-[320px] overflow-hidden">
        {cover ? (
          <img src={assetUrl(cover)} alt={b.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1B2E24] to-[#0C1510]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#12201A] via-[#12201A]/50 to-[#12201A]/40" />
        <div className="relative z-10 h-full max-w-[1100px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-8">
          <Link to="/builders" className="inline-flex items-center gap-2 text-[#C9B79C] text-sm mb-4 w-fit"><ArrowLeft size={15} /> All builders</Link>
          <div className="flex items-center gap-4">
            {b.logo_url && (
              <div className="w-24 h-24 shrink-0 rounded-2xl overflow-hidden border border-[#F3EEE3]/15 shadow-lg">
                <img src={assetUrl(b.logo_url)} alt={b.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div>
              <h1 className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">{b.name}</h1>
              <p className="text-[#F3EEE3]/60 text-sm flex items-center gap-1.5 mt-1"><MapPin size={13} className="text-[#C9B79C]" /> {b.locale}</p>
              {b.gallery?.length >= 3 && (
                <button onClick={() => setTour(true)} data-testid="start-tour-btn" className="mt-4 mr-3 inline-flex items-center gap-2 bg-[#C9B79C] text-[#12201A] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#d8c8ac] transition-colors">
                  <Play size={15} /> Take a show-home tour
                </button>
              )}
              {b.website && (
                <a href={b.website} target="_blank" rel="noreferrer" data-testid="builder-website-btn" className="mt-4 inline-flex items-center gap-2 border border-[#F3EEE3]/40 text-[#F3EEE3] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#F3EEE3]/10 transition-colors">
                  Visit website <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-12">
        {/* highlights infographic */}
        {b.stats?.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="builder-stats">
            {b.stats.map((s, i) => (
              <div key={i} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5" data-testid={`builder-stat-${i}`}>
                <p className="font-display text-2xl md:text-[1.7rem] text-[#C9B79C] leading-tight">{s.value}</p>
                <p className="text-[#F3EEE3]/55 text-xs mt-2 leading-snug">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            {b.tagline && <p className="font-display text-2xl text-[#F3EEE3] italic-display mb-4">“{b.tagline}”</p>}
            <p className="text-[#F3EEE3]/75 leading-relaxed whitespace-pre-line">{b.description}</p>
            {b.specialties?.length > 0 && (
              <div className="mt-8" data-testid="builder-services">
                <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-4">Services offered</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {b.specialties.map((s, i) => {
                    const Icon = serviceIcon(s);
                    return (
                      <div key={i} data-testid={`builder-service-${i}`} className="flex items-start gap-3 rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-4 hover:border-[#C9B79C]/40 transition-colors">
                        <span className="shrink-0 w-9 h-9 rounded-lg bg-[#C9B79C]/15 flex items-center justify-center"><Icon size={17} className="text-[#C9B79C]" /></span>
                        <span className="text-[#F3EEE3]/85 text-sm leading-snug pt-1.5">{s}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* contact card */}
          <div className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-6 h-fit" data-testid="builder-contact-card">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-4">Contact</p>
            <div className="space-y-3 text-sm">
              {b.phone && (
                <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-[#F3EEE3]/80 hover:text-[#F3EEE3]">
                  <Phone size={15} className="text-[#C9B79C] shrink-0" />
                  <span>{b.phone}{b.cell ? <span className="text-[#F3EEE3]/40 text-xs ml-1.5">· Office</span> : null}</span>
                </a>
              )}
              {b.cell && (
                <a href={`tel:${b.cell.replace(/\s/g, "")}`} className="flex items-center gap-3 text-[#F3EEE3]/80 hover:text-[#F3EEE3]">
                  <Smartphone size={15} className="text-[#C9B79C] shrink-0" />
                  <span>{b.cell}<span className="text-[#F3EEE3]/40 text-xs ml-1.5">· Cell</span></span>
                </a>
              )}
              {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-3 text-[#F3EEE3]/80 hover:text-[#F3EEE3] break-all"><Mail size={15} className="text-[#C9B79C] shrink-0" /> {b.email}</a>}
              {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[#F3EEE3]/80 hover:text-[#F3EEE3]"><ExternalLink size={15} className="text-[#C9B79C] shrink-0" /> Visit website</a>}
              {b.instagram && <a href={instaUrl(b.instagram)} target="_blank" rel="noreferrer" data-testid="builder-instagram" className="flex items-center gap-3 text-[#F3EEE3]/80 hover:text-[#F3EEE3]"><Instagram size={15} className="text-[#C9B79C] shrink-0" /> {b.instagram}</a>}
              {!b.phone && !b.email && !b.website && <p className="text-[#F3EEE3]/40">Contact us to be connected.</p>}
            </div>
            <button onClick={() => setEnq(true)} data-testid="builder-enquire-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-5 py-3 rounded-full font-medium hover:bg-white transition-colors text-sm">
              <Send size={15} /> Enquire about this builder
            </button>
          </div>
        </div>

        {/* move-in ready / quick possessions */}
        {b.quick_possessions?.length > 0 && (
          <div className="mt-14" data-testid="builder-quick-possessions">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3">Move-in ready</p>
            <h2 className="font-display text-2xl text-[#F3EEE3] mb-5">Quick possession homes</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {b.quick_possessions.map((h, i) => (
                <a
                  key={i}
                  href={h.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`builder-qp-${i}`}
                  className="group rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15] hover:border-[#C9B79C]/50 transition-colors"
                >
                  {h.image && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={assetUrl(h.image)} alt={h.address || "Quick possession home"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2">
                      {h.price && <p className="text-[#C9B79C] text-lg font-medium">{h.price}</p>}
                      {h.style && <span className="shrink-0 text-[0.55rem] uppercase tracking-wide px-2 py-1 rounded-full bg-[#C9B79C]/15 text-[#C9B79C]">{h.style}</span>}
                    </div>
                    {h.address && <p className="text-[#F3EEE3] text-sm mt-1.5">{h.address}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-xs text-[#F3EEE3]/65">
                      {(h.beds || h.beds === 0) && <span className="inline-flex items-center gap-1.5"><BedDouble size={13} className="text-[#C9B79C]" /> {h.beds} bed</span>}
                      {(h.baths || h.baths === 0) && <span className="inline-flex items-center gap-1.5"><Bath size={13} className="text-[#C9B79C]" /> {h.baths} bath</span>}
                      {h.size && <span className="inline-flex items-center gap-1.5"><Ruler size={13} className="text-[#C9B79C]" /> {h.size}</span>}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* home models */}
        {b.models?.length > 0 && (
          <div className="mt-14" data-testid="builder-models">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3">Home designs</p>
            <h2 className="font-display text-2xl text-[#F3EEE3] mb-5">Models offered</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {b.models.map((m, i) => (
                <div key={i} data-testid={`builder-model-${i}`} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-6 hover:border-[#C9B79C]/40 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-display text-xl text-[#F3EEE3]">{m.name}</p>
                    {m.style && <span className="shrink-0 text-[0.6rem] tracking-wide uppercase px-2.5 py-1 rounded-full bg-[#C9B79C]/15 text-[#C9B79C]">{m.style}</span>}
                  </div>
                  {m.community && <p className="text-[#F3EEE3]/50 text-xs mt-1.5 flex items-center gap-1.5"><MapPin size={12} className="text-[#C9B79C]" /> {m.community}</p>}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-[#F3EEE3]/80 text-sm">
                    {m.size && <span className="inline-flex items-center gap-1.5"><Ruler size={14} className="text-[#C9B79C]" /> {m.size}</span>}
                    {(m.beds || m.beds === 0) && <span className="inline-flex items-center gap-1.5"><BedDouble size={14} className="text-[#C9B79C]" /> {m.beds} bed</span>}
                    {(m.baths || m.baths === 0) && <span className="inline-flex items-center gap-1.5"><Bath size={14} className="text-[#C9B79C]" /> {m.baths} bath</span>}
                  </div>
                  {m.price && <p className="text-[#C9B79C] text-sm mt-3 font-medium">{m.price}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* lots reserved for this builder */}
        {availStage && myLots.length > 0 && (
          <div className="mt-14" data-testid="builder-reserved-lots">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3">{b.name} at Deer Park Estates</p>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
              <h2 className="font-display text-2xl text-[#F3EEE3]">
                Reserved lots in {availStage.name} <span className="text-[#C9B79C]">({myLots.length})</span>
              </h2>
              <Link to={`/stages/${availStage.id}`} data-testid="builder-view-stage-reserved" className="text-[#C9B79C] hover:text-[#F3EEE3] text-sm inline-flex items-center gap-1.5">
                View on the stage plan <ArrowLeft size={13} className="rotate-180" />
              </Link>
            </div>
            <p className="text-[#F3EEE3]/60 text-sm mb-5 max-w-2xl">
              These serviced lots in {availStage.name} are secured by {b.name} for upcoming homes. Tap a lot to see its details & photos.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {myLots.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => openLot(p, i, true)}
                  data-testid={`builder-reserved-lot-${p.parcel_no}`}
                  className="text-left rounded-xl border border-[#C9B79C]/25 bg-[#C9B79C]/[0.06] p-4 hover:border-[#C9B79C]/60 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-[#F3EEE3] font-medium">Lot {p.lot_number ?? p.parcel_no}{p.block ? <span className="text-[#F3EEE3]/40 text-xs ml-1.5">Blk {p.block}</span> : null}</p>
                    {p.walkout && <span className="text-[0.55rem] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#C9B79C]/15 text-[#C9B79C]">Walkout</span>}
                  </div>
                  <div className="mt-2.5 space-y-1 text-xs text-[#F3EEE3]/65">
                    {p.area_sqft && <p className="flex items-center gap-1.5"><Ruler size={12} className="text-[#C9B79C]" /> {Number(p.area_sqft).toLocaleString()} sq ft</p>}
                    {p.frontage_m && <p className="flex items-center gap-1.5"><Home size={12} className="text-[#C9B79C]" /> {p.frontage_m} m frontage</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* available lots to build on */}
        {availStage && availLots.length > 0 && (
          <div className="mt-14" data-testid="builder-available-lots">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3">Build with {b.name} at Deer Park Estates</p>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
              <h2 className="font-display text-2xl text-[#F3EEE3]">Available lots in {availStage.name}</h2>
              <Link to={`/stages/${availStage.id}`} data-testid="builder-view-stage" className="text-[#C9B79C] hover:text-[#F3EEE3] text-sm inline-flex items-center gap-1.5">
                View on the stage plan <ArrowLeft size={13} className="rotate-180" />
              </Link>
            </div>
            <p className="text-[#F3EEE3]/60 text-sm mb-5 max-w-2xl">
              {availStage.name} is now selling. Any of these serviced lots can host a {b.name} home{b.models?.[0]?.name ? ` — such as the ${b.models[0].name}` : ""}. Tap a lot to see its details & photos.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {availLots.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => openLot(p, i, false)}
                  data-testid={`builder-lot-${p.parcel_no}`}
                  className="text-left rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-4 hover:border-[#C9B79C]/50 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-[#F3EEE3] font-medium">Lot {p.lot_number ?? p.parcel_no}{p.block ? <span className="text-[#F3EEE3]/40 text-xs ml-1.5">Blk {p.block}</span> : null}</p>
                    {p.walkout && <span className="text-[0.55rem] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#C9B79C]/15 text-[#C9B79C]">Walkout</span>}
                  </div>
                  <div className="mt-2.5 space-y-1 text-xs text-[#F3EEE3]/65">
                    {p.area_sqft && <p className="flex items-center gap-1.5"><Ruler size={12} className="text-[#C9B79C]" /> {Number(p.area_sqft).toLocaleString()} sq ft</p>}
                    {p.frontage_m && <p className="flex items-center gap-1.5"><Home size={12} className="text-[#C9B79C]" /> {p.frontage_m} m frontage</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* gallery */}
        {b.gallery?.length > 0 && (
          <div className="mt-14">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3">Gallery</p>
            <h2 className="font-display text-2xl text-[#F3EEE3] mb-5">A look at their work</h2>
            <BuilderGallerySlider images={b.gallery} onOpen={(url) => setLightbox(url)} alt={`${b.name} home`} />
          </div>
        )}

        <p className="text-[0.62rem] tracking-widest uppercase text-[#F3EEE3]/40 mt-12">
          Photos are examples of the builder's work. Final home design subject to selection and approvals.
        </p>
      </div>

      {/* lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-[#0C1510]/95 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="builder-lightbox">
          <button className="absolute top-5 right-5 text-[#F3EEE3]/70 hover:text-[#F3EEE3]"><X size={28} /></button>
          <img src={lightbox} alt={`${b.name} — enlarged home photo`} className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg" />
        </div>
      )}

      <EnquiryDialog open={enq} onClose={() => setEnq(false)} context={{ label: b.name, source: `builder:${b.name}`, builderLogo: b.logo_url, builderName: b.name }} />
      <ShowHomeTour open={tour} onClose={() => setTour(false)} images={b.gallery || []} title={`${b.name} · Show Home`} />
      <LotDetailDialog
        open={!!detail}
        parcel={detail?.parcel}
        builder={b}
        stageNumber={availStage?.number}
        photo={detail?.photo}
        description={detail?.description}
        stageLink={availStage ? `/stages/${availStage.id}` : null}
        onClose={() => setDetail(null)}
      />
    </div>
  );
};

export default BuilderDetail;
